import React, {
  ChangeEvent,
  MutableRefObject,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Select } from "radix-ui";
import { Search as SearchGlyph } from "lucide-react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { Icon } from "@/design-system/components/Icon/Icon";
import { buildLookup } from "@/utils/list";
import { Input } from "../Input/Input";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { TypeaheadSearch } from "./TypeaheadSearch";

const DEFAULT_DEBOUNCE_MS = 500;

export type SearchProps<T> = PropsWithChildren<{
  contentProps?: React.ComponentProps<typeof Select.Content>;
  inputProps?: React.ComponentProps<typeof Input>;
  rootProps?: React.ComponentProps<typeof Select.Root>;
  triggerProps?: React.ComponentProps<typeof Select.Trigger>;
  getResults: (query: string) => Promise<T[]>;
  formatResult: (result: T) => ReactNode;
  getId: (result: T) => string;
  debounceTime?: number;
  onSelect?: (result: T) => void;
  selectedValue?: T;
  disabled?: boolean;
  /**
   * Makes the trigger `Input` the query field: this string (its value) drives
   * `getResults`, and suggestions open in a non-modal list below it while the
   * user types. `rootProps`, `triggerProps`, `inputProps` and `selectedValue`
   * don't apply in this mode, and only `contentProps.className` does.
   */
  externalQuery?: string;
}>;

export function Search<T>(props: SearchProps<T>) {
  if (props.externalQuery !== undefined) {
    return (
      <TypeaheadSearch
        externalQuery={props.externalQuery}
        getResults={props.getResults}
        formatResult={props.formatResult}
        getId={props.getId}
        debounceTime={props.debounceTime ?? DEFAULT_DEBOUNCE_MS}
        onSelect={props.onSelect}
        disabled={props.disabled}
        contentClassName={props.contentProps?.className}
      >
        {props.children}
      </TypeaheadSearch>
    );
  }
  return <PanelSearch {...props} />;
}

function PanelSearch<T>({
  contentProps,
  inputProps,
  triggerProps,
  children,
  formatResult,
  getId,
  getResults,
  debounceTime = DEFAULT_DEBOUNCE_MS,
  onSelect,
  selectedValue,
  rootProps,
  disabled = false,
}: SearchProps<T>) {
  const [results, setResults] = useState<Record<string, T> | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const searchAndSetResults = useCallback(
    async (query: string) => {
      setIsLoading(true);
      const rows = await getResults(query);
      const lookupMap = buildLookup(rows, getId);
      setResults(Object.fromEntries(lookupMap.entries()));
      setIsLoading(false);
    },
    [getResults, getId],
  );

  useEffect(() => {
    if (results === null) {
      searchAndSetResults("");
    }
  }, [results, searchAndSetResults]);

  const timeoutRef = useRef(0);
  const handleQueryChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const query = e.currentTarget.value;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        searchAndSetResults(query);
      }, debounceTime);
    },
    [debounceTime, searchAndSetResults],
  );

  const handleSelect = useCallback(
    (id: string) => {
      const result = results?.[id];
      if (result) {
        onSelect?.(result);
      }
    },
    [onSelect, results],
  );

  const resultsList = useMemo(() => {
    if (!results) return [];
    return Object.values(results);
  }, [results]);

  const localInputRef = useRef<HTMLInputElement>(null);

  const clearContentQuery = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    const inputElement = localInputRef.current;
    if (inputElement) {
      inputElement.value = "";
    }
    void searchAndSetResults("");
  }, [searchAndSetResults]);

  const { onOpenChange: rootOnOpenChange, ...rootRest } = rootProps ?? {};

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      rootOnOpenChange?.(nextOpen);
      if (!nextOpen) {
        clearContentQuery();
      }
      if (disabled) {
        return;
      }

      const inputElement =
        (inputProps?.ref as MutableRefObject<HTMLInputElement> | undefined)
          ?.current || localInputRef.current;

      if (nextOpen) {
        setTimeout(() => inputElement?.focus(), 0);
      }
    },
    [clearContentQuery, disabled, inputProps?.ref, rootOnOpenChange],
  );

  return (
    <Select.Root
      disabled={disabled}
      onValueChange={handleSelect}
      value={selectedValue && getId(selectedValue)}
      {...rootRest}
      onOpenChange={handleOpenChange}
    >
      <Select.Trigger
        {...triggerProps}
        asChild={triggerProps?.asChild ?? true}
        disabled={disabled}
      >
        {children}
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          align="start"
          sideOffset={4}
          {...contentProps}
          className={twMerge(
            clsx(
              "data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut bg-surface-default z-popover shadow-elevation-200 divide-y rounded-lg",
              "w-[var(--radix-select-trigger-width)] min-w-[var(--radix-select-trigger-width)]",
              contentProps?.className,
            ),
          )}
        >
          <div className={clsx("w-full p-3")}>
            <Input
              onChange={handleQueryChange}
              leadingIcon={
                <Icon
                  icon={SearchGlyph}
                  size="md"
                  className="text-iconDefault"
                />
              }
              autoFocus
              trailingIcon={isLoading && <LoadingSpinner />}
              autoComplete="false"
              ref={localInputRef}
              {...inputProps}
              className={twMerge("w-full min-w-0 p-3", inputProps?.className)}
            />
          </div>

          <Select.Viewport className="max-h-96 min-h-0 overflow-y-auto px-3 py-2">
            {!!resultsList.length &&
              resultsList.map((result) => (
                <Select.Item
                  key={getId(result)}
                  value={getId(result)}
                  className="data-[highlighted]:bg-surface-hover text-textHighEmphasis cursor-pointer rounded-lg px-4 py-[6px] outline-none"
                >
                  {formatResult(result)}
                </Select.Item>
              ))}
            {!resultsList?.length && !isLoading && (
              <div className="text-textMediumEmphasis px-4 py-2">
                No results found
              </div>
            )}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
